import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import GroupCard from "@/components/base/GroupCard";
import RankRow from "@/components/base/RankRow";
import { groups } from "@/mocks/groups";
import { categories } from "@/mocks/categories";
import { site } from "@/mocks/site";

const sortOptions = [
  { key: "score", label: "综合推荐" },
  { key: "activity", label: "活跃度" },
  { key: "members", label: "群成员数" },
  { key: "rating", label: "推荐指数" },
] as const;

type SortKey = (typeof sortOptions)[number]["key"];

export default function Ranking() {
  const [category, setCategory] = useState("全部");
  const [sortKey, setSortKey] = useState<SortKey>("score");

  const tabs = ["全部", ...categories.map((item) => item.name)];

  const displayed = useMemo(() => {
    const base = category === "全部" ? [...groups] : groups.filter((item) => item.category === category);
    base.sort((a, b) => {
      const diff = (b[sortKey] as number) - (a[sortKey] as number);
      return diff !== 0 ? diff : a.rank - b.rank;
    });
    return base.map((item, index) => ({ ...item, rank: index + 1 }));
  }, [category, sortKey]);

  const top3 = displayed.slice(0, 3);
  const rest = displayed.slice(3);

  const jsonLd = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "51吃瓜群排行榜",
        description:
          "51吃瓜群综合排行榜，按活跃度、管理规范、成员口碑与信息质量综合评分，支持按娱乐八卦、影视追剧、网红热点等方向筛选。",
        inLanguage: "zh-CN",
        url: `${origin}/ranking`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "群组排行", item: `${origin}/ranking` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "51吃瓜群综合排行榜",
        itemListElement: groups
          .slice()
          .sort((a, b) => b.score - a.score)
          .map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            description: item.summary,
          })),
      },
    ];
  }, []);

  useSeo({
    title: "51吃瓜群排行 - 综合评分排行榜与分类筛选",
    description:
      "51吃瓜群综合排行榜，按活跃度、管理规范、成员口碑与信息质量给出综合评分，支持娱乐八卦、影视追剧、网红热点、生活趣事、时事热点、游戏电竞六个方向筛选，帮你快速锁定高质量吃瓜群。",
    keywords: "51吃瓜群排行,吃瓜群排行榜,51吃瓜群哪个好,吃瓜群推荐,群组排行",
    path: "/ranking",
    jsonLd,
  });

  return (
    <>
      <PageHeader
        eyebrow="实时更新"
        title="51吃瓜群综合排行榜"
        desc={`榜单每两周复核一次，综合考察活跃度、管理规范、成员口碑与信息质量。${site.updateNote}，长期沉寂或广告泛滥的群组会下调排名。`}
        crumbs={[{ label: "首页", to: "/" }, { label: "群组排行" }]}
      />

      <section className="w-full bg-background-50 px-4 py-10 md:px-6 md:py-14">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-2">
                <span className="font-label text-xs text-foreground-500">按主题筛选</span>
                <div className="flex flex-wrap gap-2">
                  {tabs.map((tab) => {
                    const active = tab === category;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setCategory(tab)}
                        aria-pressed={active}
                        className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-xs transition-colors md:text-sm ${
                          active
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-100 text-foreground-600 hover:bg-background-200"
                        }`}
                      >
                        {tab}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <label htmlFor="sort" className="font-label text-xs text-foreground-500">
                  排序方式
                </label>
                <select
                  id="sort"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SortKey)}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2 font-label text-sm text-foreground-800 outline-none focus:border-primary-400"
                >
                  {sortOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <p className="mt-4 flex items-center gap-2 border-t border-background-200 pt-4 text-xs text-foreground-500">
              <i className="ri-list-check-2"></i>
              当前「{category}」共 {displayed.length} 个群组，按「
              {sortOptions.find((item) => item.key === sortKey)?.label}」排序
            </p>
          </div>

          {top3.length > 0 ? (
            <div className="mt-10">
              <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950 md:text-2xl">
                <i className="ri-medal-2-line text-primary-500"></i>
                本榜前三
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {top3.map((group) => (
                  <GroupCard key={group.id} group={group} />
                ))}
              </div>
            </div>
          ) : null}

          {rest.length > 0 ? (
            <div className="mt-10">
              <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950 md:text-2xl">
                <i className="ri-list-ordered text-accent-600"></i>
                第 4 名及以后
              </h2>
              <div className="mt-5 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="hidden items-center gap-4 border-b border-background-200 bg-background-100 px-4 py-3 font-label text-xs text-foreground-500 md:flex">
                  <span className="w-8 text-center">排名</span>
                  <span className="flex-1">群组</span>
                  <span className="w-32">活跃度</span>
                  <span className="w-14 text-right">推荐指数</span>
                </div>
                {rest.map((group) => (
                  <RankRow key={group.id} group={group} />
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-5 md:flex-row md:items-center">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className="ri-lightbulb-line text-lg"></i>
              </span>
              <div>
                <p className="font-label text-sm font-medium text-foreground-900">排名只是参考，适合自己才重要</p>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                  不同的人偏好不同，建议先读一遍选群指南，再结合榜单挑出三到五个候选群慢慢体验。
                </p>
              </div>
            </div>
            <Link
              to="/guide"
              className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              阅读选群指南
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}